import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
type CommunityInfo = {
  name: string;
  path: string;
  description: string;
  image: string;
};
const allCommunities: Record<string, CommunityInfo> = {
  Summerlin: {
    name: 'Summerlin',
    path: '/summerlin',
    description: 'Master-planned luxury with Red Rock Canyon views, 250+ parks, and championship golf.',
    image: 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=2940&auto=format&fit=crop'
  },
  'Southern Highlands': {
    name: 'Southern Highlands',
    path: '/southern-highlands',
    description: 'Exclusive gated community with championship golf and Mediterranean-inspired estates.',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=2950&auto=format&fit=crop'
  },
  Henderson: {
    name: 'Henderson',
    path: '/henderson',
    description: "Nevada's second-largest city — one of America's safest with upscale living in Green Valley and Anthem.",
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=2906&auto=format&fit=crop'
  },
  'MacDonald Highlands': {
    name: 'MacDonald Highlands',
    path: '/macdonald-highlands',
    description: 'Ultra-luxury gated enclave with DragonRidge Country Club and panoramic Strip views.',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2940&auto=format&fit=crop'
  },
  'Spring Valley': {
    name: 'Spring Valley',
    path: '/spring-valley',
    description: 'Centrally located with diverse luxury options, The Lakes, and easy Strip access.',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?q=80&w=2940&auto=format&fit=crop'
  },
  Southwest: {
    name: 'Southwest Las Vegas',
    path: '/southwest',
    description: 'Fast-growing area with Mountains Edge, Rhodes Ranch, and Durango Casino & Resort.',
    image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?q=80&w=2787&auto=format&fit=crop'
  },
  'North Las Vegas': {
    name: 'North Las Vegas',
    path: '/north-las-vegas',
    description: 'Emerging market with Aliante, new master-planned communities, and strong investment potential.',
    image: 'https://images.unsplash.com/photo-1512915922686-57c11dde9b6b?q=80&w=2946&auto=format&fit=crop'
  },
  'Green Valley': {
    name: 'Green Valley',
    path: '/green-valley',
    description: 'Established upscale Henderson community with top schools, beautiful parks, and The District.',
    image: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=2792&auto=format&fit=crop'
  }
};
const relatedMap: Record<string, string[]> = {
  Summerlin: ['Southern Highlands', 'Spring Valley', 'Southwest'],
  'Southern Highlands': ['Summerlin', 'Southwest', 'Henderson'],
  Henderson: ['Green Valley', 'MacDonald Highlands', 'Southern Highlands'],
  'MacDonald Highlands': ['Henderson', 'Green Valley', 'Southern Highlands'],
  'Spring Valley': ['Summerlin', 'Southwest', 'Henderson'],
  Southwest: ['Spring Valley', 'Summerlin', 'Southern Highlands'],
  'North Las Vegas': ['Henderson', 'Spring Valley', 'Southwest'],
  'Green Valley': ['Henderson', 'MacDonald Highlands', 'Summerlin']
};
type RelatedCommunitiesProps = {
  currentCommunity: string;
};
export function RelatedCommunities({
  currentCommunity
}: RelatedCommunitiesProps) {
  const relatedNames = relatedMap[currentCommunity] || [];
  const related = relatedNames.map(name => allCommunities[name]).filter(Boolean);
  if (related.length === 0) return null;
  return <section className="py-24 bg-warmGray px-6 border-y border-warmGray-dark" data-id="element-503">
      <div className="max-w-7xl mx-auto" data-id="element-504">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-505">
          <span className="text-gold uppercase tracking-[0.2em] text-sm font-medium mb-4 block" data-id="element-506">
            Continue Exploring
          </span>
          <h2 className="text-4xl font-serif text-charcoal" data-id="element-507">
            More Las Vegas Communities
          </h2>
          <p className="text-charcoal-light font-light max-w-2xl mx-auto mt-4" data-id="element-508">
            Discover other premier neighborhoods where Jenny Gong can help you
            find your perfect home.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-509">
          {related.map((community, index) => <motion.div key={community.name} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} data-id="element-510">
              <Link to={community.path} className="group block" data-id="element-511">
                <div className="relative h-56 mb-6 overflow-hidden" data-id="element-512">
                  <div className="absolute inset-0 bg-charcoal/20 group-hover:bg-transparent transition-colors duration-500 z-10" data-id="element-513" />
                  <img src={community.image} alt={`Luxury homes for sale in ${community.name}, Las Vegas Nevada`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-514" />
                </div>
                <h3 className="text-2xl font-serif text-charcoal mb-2 group-hover:text-gold transition-colors duration-300" data-id="element-515">
                  {community.name}
                </h3>
                <p className="text-charcoal-light font-light text-sm mb-4 line-clamp-2" data-id="element-516">
                  {community.description}
                </p>
                <div className="flex items-center text-gold uppercase tracking-widest text-sm font-medium" data-id="element-517">
                  <span className="mr-2" data-id="element-518">View Homes</span>
                  <ArrowRightIcon className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-2" data-id="element-519" />
                </div>
              </Link>
            </motion.div>)}
        </div>
      </div>
    </section>;
}